/**
 * EntraPrincipalChip — footer identity indicator per UI-SPEC §"Authenticated-principal indicator".
 *
 * NEW component for Phase 4 (no direct analog in packages/spa). Reads from
 * useCroMe() hook which calls GET /api/cro/me (Plan 04-04 endpoint, ENTRA-02).
 *
 * States:
 *   - Loading: Skeleton placeholder (h-6 w-48 rounded-full)
 *   - Member (isCroGroupMember === true): green chip
 *     "Signed in as {upn} · sg-saledash-cro"
 *   - Non-member fallback (isCroGroupMember === false): red chip
 *     "Signed in as {upn} · Not in CRO group"
 *     This case normally can't happen — Easy Auth platform gate filters non-members
 *     before the request reaches the SPA. Defense-in-depth surfaces a clear UX
 *     warning if it does occur.
 *   - Error: text-ink-500 "Identity unavailable"
 *
 * Per ENTRA-02 the chip surfaces both UPN AND group-membership claim.
 */
import { useCroMe } from '../queries/useCroMe.js';

import { Skeleton } from './ui/Skeleton.js';

export function EntraPrincipalChip(): React.JSX.Element {
  const { data, isLoading, isError } = useCroMe();

  if (isLoading) {
    return <Skeleton className="h-6 w-48 rounded-full" />;
  }

  if (isError || !data) {
    return (
      <span className="text-xs text-ink-500" data-testid="entra-chip-error">
        Identity unavailable
      </span>
    );
  }

  if (!data.isCroGroupMember) {
    // Defense-in-depth: should not happen under normal Easy Auth conditions,
    // but surface a clear warning if it does.
    return (
      <span
        className="inline-flex items-center rounded-full bg-bad-bg px-3 py-1 text-xs font-medium text-bad-text whitespace-nowrap"
        data-testid="entra-chip-non-member"
        aria-label={`Signed in as ${data.upn} — not in CRO group`}
      >
        Signed in as {data.upn} &middot; Not in CRO group
      </span>
    );
  }

  return (
    <span
      className="inline-flex items-center rounded-full bg-ink-100 px-3 py-1 text-xs text-ink-600 whitespace-nowrap"
      data-testid="entra-chip-member"
      aria-label={`Signed in as ${data.upn} — CRO group member`}
    >
      Signed in as {data.upn} &middot; sg-saledash-cro
    </span>
  );
}
